import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withCtx as _withCtx, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, renderSlot as _renderSlot, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "flex flex-1 gap-1 items-start min-w-0" }
const _hoisted_2 = {
  class: "bg-gray-200 rounded-md px-2 py-0.5 whitespace-nowrap truncate",
  style: { fontSize: '11px' }
}
const _hoisted_3 = {
  key: 0,
  class: "text-xs"
}
const _hoisted_4 = {
  key: 0,
  class: "whitespace-nowrap flex gap-1"
}
const _hoisted_5 = {
  key: 1,
  class: "whitespace-nowrap flex gap-1"
}
const _hoisted_6 = { class: "min-w-[130px]" }
const _hoisted_7 = {
  key: 1,
  class: "bg-gray-200 rounded-md px-2 py-0.5 whitespace-nowrap truncate",
  style: { fontSize: '11px' }
}
const _hoisted_8 = {
  class: "bg-gray-200 rounded-md px-2 py-0.5 whitespace-nowrap truncate",
  style: { fontSize: '11px' }
}

import { format } from "date-fns";
import deepEqual from "deep-equal";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import OaiTooltip from "shared/components/other/OaiTooltip.vue";
import { defaultPlotFilters } from "@/composables/report";
import { ReportFilters, ReportPlotType, ReportSummaryContext } from "@/types/Report";
import { ReportDateFilter } from "@/types/reports/filters";

type KeysOfUnion<T> = T extends T ? keyof T : never;
type ReportFilterType = KeysOfUnion<ReportFilters>;

const dateRangeFilterKey: ReportFilterType = "daterange";
const dateFormat = "dd.MM.yyyy";


export default /*#__PURE__*/_defineComponent({
  __name: 'ReportFilterBadges',
  props: {
    type: {},
    filters: {},
    aggregationLabel: {},
    reportSummaryContext: {},
    height: {},
    hideDateFilter: { type: Boolean },
    hideNonEmptyFilters: { type: Boolean },
    showPreviousPeriodBadge: { type: Boolean }
  },
  setup(__props: any) {

const props = __props;

const { t } = useI18n();

const dateFilterLabels: Record<ReportDateFilter["type"], string> = {
  last_day: t("time.last_day"),
  last_week: t("time.last_week", 1),
  last_calendar_week: t("time.last_calendar_week"),
  last_2_weeks: t("time.last_week", 2),
  last_month: t("time.last_month", 1),
  last_3_months: t("time.last_month", 3),
  last_6_months: t("time.last_month", 6),
  last_12_months: t("time.last_month", 12),
  date_range: t("analytics.reports.absolute_date"),
  total: t("time.all_time"),
};

const filterLabels = computed<Record<ReportFilterType, string>>(() => ({
  location: t("analytics.reports.location"),
  processes:
    "processes" in props.filters && props.filters.processes.mode === "component"
      ? t("analytics.reports.preset_mode")
      : t("analytics.reports.process_mode"),
  unit_value_type: t("analytics.reports.unit_value"),
  daterange: t("buttons.time_range"),
}));

const nonEmptyFilters = computed(() => {
  const defaultFilters = defaultPlotFilters[props.type]();
  return Object.entries(props.filters)
    .filter(([key, value]) => {
      const defaultFilter = (defaultFilters as Record<string, unknown>)[key];
      return key !== dateRangeFilterKey && !deepEqual(defaultFilter, value);
    })
    .map(([key, value]) => ({
      type: key as ReportFilterType,
      filter: value,
    }));
});

const dateFilterLabel = computed(() => {
  if (dateRangeFilterKey in props.filters) {
    if (
      props.filters.daterange.type === "date_range" &&
      props.filters.daterange.start_date &&
      props.filters.daterange.end_date
    ) {
      return `${format(props.filters.daterange.start_date, dateFormat)}-${format(
        props.filters.daterange.end_date,
        dateFormat,
      )}`;
    }
    const label = dateFilterLabels[props.filters.daterange.type];

    if (props.showPreviousPeriodBadge) {
      return `${label} | ${t("analytics.reports.previous_period_label")}`;
    }

    return label;
  }
  return undefined;
});

const formatPeriod = (start: Date, end: Date) => {
  if (start.getTime() === end.getTime()) {
    return format(start, dateFormat);
  }
  return `${format(start, dateFormat)}-${format(end, dateFormat)}`;
};

const formattedPeriod = computed(() => {
  const context = props.reportSummaryContext;
  if (!context || !context.start_date || !context.end_date) {
    return null;
  }
  return formatPeriod(context.start_date, context.end_date);
});

const formattedPreviousPeriod = computed(() => {
  const context = props.reportSummaryContext;
  if (!context || !context.previous_period_start_date || !context.previous_period_end_date) {
    return null;
  }
  return formatPeriod(context.previous_period_start_date, context.previous_period_end_date);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    style: _normalizeStyle({ height: _ctx.height && `${_ctx.height}px` }),
    class: "flex gap-1"
  }, [
    _createElementVNode("div", _hoisted_1, [
      (dateFilterLabel.value && !_ctx.hideDateFilter)
        ? (_openBlock(), _createBlock(OaiTooltip, {
            key: 0,
            simple: ""
          }, {
            tooltip: _withCtx(() => [
              (formattedPeriod.value || formattedPreviousPeriod.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                    (formattedPeriod.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                          _createElementVNode("div", {
                            class: _normalizeClass({ 'min-w-[130px]': formattedPreviousPeriod.value })
                          }, _toDisplayString(_unref(t)("analytics.reports.period")) + ": ", 3),
                          _createElementVNode("div", null, _toDisplayString(formattedPeriod.value), 1)
                        ]))
                      : _createCommentVNode("", true),
                    (formattedPreviousPeriod.value)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                          _createElementVNode("div", _hoisted_6, _toDisplayString(_unref(t)("analytics.reports.previous_period")) + ":", 1),
                          _createElementVNode("div", null, _toDisplayString(formattedPreviousPeriod.value), 1)
                        ]))
                      : _createCommentVNode("", true)
                  ]))
                : _createCommentVNode("", true)
            ]),
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_2, _toDisplayString(dateFilterLabel.value), 1)
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      (_ctx.aggregationLabel)
        ? (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(_ctx.aggregationLabel), 1))
        : _createCommentVNode("", true),
      (nonEmptyFilters.value.length > 0 && !_ctx.hideNonEmptyFilters)
        ? (_openBlock(), _createBlock(OaiTooltip, {
            key: 2,
            simple: ""
          }, {
            tooltip: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(nonEmptyFilters.value, (filter) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: filter.type,
                  class: "whitespace-nowrap text-xs"
                }, _toDisplayString(filterLabels.value[filter.type]), 1))
              }), 128))
            ]),
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_8, _toDisplayString(_unref(t)("analytics.reports.filters")) + " (" + _toDisplayString(nonEmptyFilters.value.length) + ") ", 1)
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      _renderSlot(_ctx.$slots, "badges")
    ]),
    _renderSlot(_ctx.$slots, "default")
  ], 4))
}
}

})